/** API-Hooks E14: Filialen, Events, Kommission, Import, Newsletter. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";
import type { EventEingabe, ImportArt } from "@shared/schemas/wachstum";

export interface Filiale {
  id: string;
  name: string;
  slug: string;
  aktuell: boolean;
  regale: number;
  frei: number;
  baldFrei: number;
  mieter: number;
  verkaeufeHeute: number;
  umsatzHeuteCent: number;
  offeneAufgaben: number;
}
export const useFilialen = () => useQuery({ queryKey: ["laden", "filialen"], queryFn: () => rufe<{ laeden: Filiale[] }>(api.api.laden.filialen.$get()) });
export const useFilialMieter = (an: boolean) => useQuery({ queryKey: ["laden", "filialen", "mieter"], queryFn: () => rufe<{ kandidaten: { mieterId: string; quelleLadenId: string; quelleLaden: string; name: string; email: string; mieternummer: string }[] }>(api.api.laden.filialen.mieter.$get()), enabled: an });
export function useFilialMieterUebernehmen() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (d: { quelleLadenId: string; mieterId: string }) => rufe<{ id: string; mieternummer: string }>(api.api.laden.filialen.mieter.uebernehmen.$post({ json: d })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }) });
}
export function useLadenWechseln() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ladenId: string) => rufe(api.api.auth.laden.$post({ json: { ladenId } })),
    onSuccess: () => {
      qc.clear();
      window.location.assign("/laden");
    },
  });
}

export interface EventZeile {
  id: string;
  name: string;
  text: string;
  von: string;
  bis: string;
  provisionProzent: number | null;
  rabattProzent: number | null;
  neuigkeitId: string | null;
  erstelltAt: string;
  abgesagtAt: string | null;
}
export const useEvents = () => useQuery({ queryKey: ["laden", "events"], queryFn: () => rufe<{ events: EventZeile[] }>(api.api.laden.events.$get()) });
export function useEventAnlegen() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (d: EventEingabe) => rufe<{ id: string; benachrichtigt: number }>(api.api.laden.events.$post({ json: d })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }) });
}
export function useEventAbsagen() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => rufe(api.api.laden.events[":id"].absagen.$post({ param: { id } })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }) });
}

export function useKommissionAnnehmen() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (d: { mieterId: string; bezeichnung: string; preisCent: number; anzahl: number }) => rufe<{ artikel: { id: string; code: string }[] }>(api.api.laden.kommission.$post({ json: { ...d, kategorie: "", groesse: "" } })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden", "artikel"] }) });
}

export interface ImportVorschau {
  spalten: string[];
  zuordnung: Record<string, number | null>;
  beispiele: string[][];
  anzahl: number;
  felder: { feld: string; label: string; pflicht?: boolean }[];
}
export const useImportVorschau = () => useMutation({ mutationFn: (d: { art: ImportArt; dateiname: string; base64?: string; text?: string }) => rufe<ImportVorschau>(api.api.laden.import.vorschau.$post({ json: d })) });
export function useImport() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (d: { art: ImportArt; dateiname: string; base64?: string; text?: string; zuordnung: Record<string, number | null> }) => rufe<{ angelegt: number; uebersprungen: number; fehler: { zeile: number; grund: string }[] }>(api.api.laden.import.$post({ json: d })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }) });
}

export interface NewsletterStand {
  bestaetigt: number;
  offen: number;
  abgemeldet: number;
  aktiv: boolean;
  neuImLaden: boolean;
  versand: { id: string; betreff: string; text: string; anzahl: number; erstelltAt: string }[];
}
export const useNewsletter = () => useQuery({ queryKey: ["laden", "newsletter"], queryFn: () => rufe<NewsletterStand>(api.api.laden.newsletter.$get()) });
export function useNewsletterSenden() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (d: { betreff: string; text: string; mitNeuheiten: boolean }) => rufe<{ anzahl: number }>(api.api.laden.newsletter.senden.$post({ json: d })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden", "newsletter"] }) });
}
